import { formatUserName } from "@impero-com/common/utils/formatting";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { TableToolbar, TextTool } from "@impero-com/ui";
import { useOutletContext } from "react-router-dom";

import { AssignmentTable } from "org/user/components/AssignmentTable";
import { ReassignmentFailureAlert } from "org/user/components/ReassignmentFailureAlert";
import { RemoveUserModal } from "org/user/components/RemoveUserModal";
import { ReplaceUserModal } from "org/user/components/ReplaceUserModal";
import { useRiskDirectoriesForUserQuery } from "org/user/queries/useRiskDirectoriesForUserQuery";
import { OutletContext } from "org/user/UserViewPage";
import { getFilterModeConfig } from "utils/table/getFilterModeConfig";

import { describeRiskDirectoryFailure } from "./describeRiskDirectoryFailure";
import { useRemoveRiskDirectoryAccessRightUtils } from "./useRemoveRiskDirectoryAccessRightUtils";
import { useReplaceRiskDirectoryUserUtils } from "./useReplaceRiskDirectoryUserUtils";
import {
    RiskDirectoryRow,
    useUserRiskDirectoriesListTable,
} from "./useUserRiskDirectoriesListTable";

export function UserRiskDirectoriesList() {
    const { user } = useOutletContext<OutletContext>();
    const userId = user.id;
    const { data: riskDirectories } = useRiskDirectoriesForUserQuery(userId);
    const { t } = useTranslation();

    const { table, getFilterComponent, tableId } =
        useUserRiskDirectoriesListTable(riskDirectories);

    const modeConfig = getFilterModeConfig(t);

    const selectedRows = table.getFilteredSelectedRowModel().rows;
    const deselectAll = () => table.resetRowSelection();

    const {
        removeAccessRightToolState,
        isRemoveAccessRightModalOpen,
        removeAccessRightError,
        openRemoveAccessRightModal,
        closeRemoveAccessRightModal,
        removeAccessRight,
        isRemovingAccessRight,
    } = useRemoveRiskDirectoryAccessRightUtils({
        userId,
        selectedRows,
        deselectAll,
    });

    const {
        replaceUserToolState,
        isReplaceUserModalOpen,
        replaceUserError,
        openReplaceUserModal,
        closeReplaceUserModal,
        replaceUser,
        isReplacingUser,
    } = useReplaceRiskDirectoryUserUtils({ userId, selectedRows, deselectAll });

    const removeFailure = describeRiskDirectoryFailure(
        t,
        removeAccessRightError,
        riskDirectories
    );
    const replaceFailure = describeRiskDirectoryFailure(
        t,
        replaceUserError,
        riskDirectories
    );

    return (
        <div>
            <RemoveUserModal
                onClose={closeRemoveAccessRightModal}
                onSubmit={removeAccessRight}
                open={isRemoveAccessRightModalOpen}
                loading={isRemovingAccessRight}
                message={t(
                    "AccessAndAssignment_RiskDirectories_RemoveAccessRightMessage",
                    {
                        user: formatUserName(user),
                        count: selectedRows.length,
                    }
                )}
                error={
                    removeFailure && (
                        <ReassignmentFailureAlert failure={removeFailure} />
                    )
                }
            />
            <ReplaceUserModal
                open={isReplaceUserModalOpen}
                replaceType="WithUser"
                loading={isReplacingUser}
                onClose={closeReplaceUserModal}
                onClick={replaceUser}
                user={user}
                error={
                    replaceFailure && (
                        <ReassignmentFailureAlert failure={replaceFailure} />
                    )
                }
            />
            <AssignmentTable<RiskDirectoryRow>
                table={table}
                tableId={tableId}
                getFilterComponent={getFilterComponent}
                modeConfig={modeConfig}
                toolbar={
                    <TableToolbar>
                        <TextTool
                            state={removeAccessRightToolState}
                            onClick={openRemoveAccessRightModal}
                        >
                            {t(
                                "AccessAndAssignment_RiskDirectories_RemoveAccessRight"
                            )}
                        </TextTool>
                        <TextTool
                            state={replaceUserToolState}
                            onClick={openReplaceUserModal}
                        >
                            {t("AccessAndAssignment_Tool_ReplaceUser")}
                        </TextTool>
                    </TableToolbar>
                }
            />
        </div>
    );
}
